const IGNORED_KEYS = new Set([
  'stack',
  'url',
  'statusCode',
  'statusMessage',
  'error',
  'success',
  'code',
  'status',
  'httpStatus',
  'request',
  'response'
])

const PRIORITY_KEYS = new Set(['message', 'detail', 'error_description', 'errorMessage'])

export function extractApiErrorMessage(source: unknown, fallback = "Une erreur s'est produite"): string {
  const messages: string[] = []
  const visited = new WeakSet<object>()

  const pushMessage = (value: unknown) => {
    if (typeof value !== 'string' && typeof value !== 'number') return
    let text = String(value).trim()
    if (!text) return

    text = text.replace(/^[a-zàâäçéèêëîïôöùûüÿñ]/i, (match) => match.toLocaleUpperCase())

    const alreadyExists = messages.some((msg) => msg.toLocaleLowerCase() === text.toLocaleLowerCase())
    if (!alreadyExists) {
      messages.push(text)
    }
  }

  const toLabel = (key: string) => {
    const clean = key.replaceAll('_', ' ').trim()
    if (!clean) return ''
    return clean.charAt(0).toLocaleUpperCase() + clean.slice(1)
  }

  const formatKeyValue = (key: string, value: unknown) => {
    if (PRIORITY_KEYS.has(key) || IGNORED_KEYS.has(key)) return
    const label = toLabel(key)
    if (!label) return
    if (Array.isArray(value)) {
      const filtered = value
        .map((item) => {
          if (typeof item === 'string') {
            const trimmed = item.trim()
            // Ignorer les lignes de stack trace
            if (!trimmed || trimmed.startsWith('at ') || trimmed.includes('\\node_modules\\') || trimmed.startsWith('http')) {
              return ''
            }
            return trimmed
          }
          if (typeof item === 'number') return String(item)
          return ''
        })
        .filter(Boolean)
      if (filtered.length) {
        // Si la clé est "data" et que c'est un tableau de messages d'erreur, ne pas ajouter le préfixe "Data : "
        // Afficher directement les messages
        if (key === 'data' && filtered.length > 0) {
          filtered.forEach(msg => pushMessage(msg))
        } else {
          pushMessage(`${label} : ${filtered.join(', ')}`)
        }
      }
      return
    }
    if (typeof value === 'number') {
      pushMessage(`${label} : ${value}`)
      return
    }
    if (typeof value === 'string') {
      pushMessage(`${label} : ${value}`)
      return
    }
  }

  const traverse = (payload: unknown, depth = 0) => {
    if (!payload || depth > 5) return
    if (typeof payload === 'string' || typeof payload === 'number') {
      pushMessage(payload)
      return
    }
    if (Array.isArray(payload)) {
      payload.forEach((item) => traverse(item, depth + 1))
      return
    }
    if (typeof payload === 'object') {
      if (visited.has(payload)) return
      visited.add(payload)

      const obj = payload as Record<string, unknown>

      PRIORITY_KEYS.forEach((key) => {
        const value = obj[key]
        if (typeof value === 'string') pushMessage(value)
      })

      Object.entries(obj).forEach(([key, value]) => {
        if (value === null || value === undefined) return
        if (IGNORED_KEYS.has(key) || PRIORITY_KEYS.has(key)) return

        if (typeof value === 'string' || typeof value === 'number' || Array.isArray(value)) {
          formatKeyValue(key, value)
          return
        }

        traverse(value, depth + 1)
      })
    }
  }

  traverse(source)

  return messages.length ? messages.join('\n') : fallback
}

